import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, IndianRupee, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { payViaRazorpay } from "@/lib/razorpay";

const METHODS = [
  { key: "razorpay", label: "Razorpay (UPI / Card / Netbanking)", emoji: "💳", live: true },
  { key: "upi", label: "UPI (manual)", emoji: "📲", live: false },
  { key: "gpay", label: "Google Pay (manual)", emoji: "🔵", live: false },
  { key: "phonepe", label: "PhonePe (manual)", emoji: "🟣", live: false },
  { key: "bank", label: "Bank Transfer (manual)", emoji: "🏦", live: false },
];

const QUICK = [500, 1000, 2000, 5000];

export default function AddMoney() {
  const nav = useNavigate();
  const { user, tenant } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("razorpay");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const onSubmit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    setLoading(true);
    try {
      if (method === "razorpay") {
        const res = await payViaRazorpay({
          endpoint: "/payments/customer-topup/order",
          verifyEndpoint: "/payments/customer-topup/verify",
          amount: amt,
          purpose: "customer_topup",
          name: tenant?.brand_name || "Add Money",
          user: { name: user?.full_name, phone: user?.phone },
        });
        setSuccess({ amount: amt, method, mocked: res.mocked });
        toast.success(res.mocked
          ? `(MOCK) ${fmtINR(amt)} added to Saving Wallet`
          : `${fmtINR(amt)} added to Saving Wallet via Razorpay`);
      } else {
        const r = await api.post("/customer/wallet/add-money", { amount: amt, method });
        setSuccess({ amount: amt, method, tx: r.data.transaction, mocked: true });
        toast.success(`${fmtINR(amt)} added (Manual)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-6" data-testid="add-money-success">
        <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mb-4">
          <CheckCircle2 className="w-12 h-12 text-emerald-600" />
        </div>
        <div className="font-extrabold text-2xl tracking-tight">Payment Successful</div>
        <div className="text-slate-500 text-sm mt-1">{fmtINR(success.amount)} via {success.method.toUpperCase()}</div>
        {success.mocked && (
          <div className="text-amber-600 text-[11px] font-bold mt-1">MOCK PAYMENT (no live Razorpay keys configured)</div>
        )}
        <button data-testid="success-go-dashboard" onClick={() => nav("/customer")} className="mt-8 bg-tenant-primary text-white px-8 py-3 rounded-2xl font-bold tap-scale">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div data-testid="add-money-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">Add Money</div>
      </div>

      <div className="px-5 pt-6">
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Enter Amount</label>
        <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white">
          <IndianRupee className="w-5 h-5 text-slate-400" />
          <input
            data-testid="add-money-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0"
            inputMode="decimal"
            className="flex-1 outline-none text-2xl font-extrabold"
          />
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2">
          {QUICK.map((q) => (
            <button key={q} data-testid={`quick-amt-${q}`} onClick={() => setAmount(String(q))} className="bg-white border border-slate-200 rounded-xl py-2.5 text-sm font-bold tap-scale">
              ₹{q}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">Select Payment Method</div>
          <div className="space-y-2">
            {METHODS.map((m) => (
              <button
                key={m.key}
                data-testid={`pay-method-${m.key}`}
                onClick={() => setMethod(m.key)}
                className={`w-full p-3.5 rounded-2xl flex items-center gap-3 border-2 tap-scale ${
                  method === m.key ? "border-tenant-primary bg-blue-50" : "border-slate-200 bg-white"
                }`}
              >
                <span className="text-xl">{m.emoji}</span>
                <span className="font-bold text-sm flex-1 text-left">{m.label}</span>
                {m.live && (
                  <span className="text-[9px] font-extrabold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                )}
                <span className={`w-5 h-5 rounded-full border-2 ${method === m.key ? "border-tenant-primary bg-tenant-primary" : "border-slate-300"}`}>
                  {method === m.key && <span className="block w-1.5 h-1.5 bg-white rounded-full m-auto mt-1"></span>}
                </span>
              </button>
            ))}
          </div>
        </div>

        <button data-testid="confirm-add-money" onClick={onSubmit} disabled={loading} className="w-full mt-6 bg-tenant-primary text-white py-4 rounded-2xl font-extrabold tap-scale disabled:opacity-60">
          {loading ? "Processing..." : `Pay ${amount ? fmtINR(amount) : ""}`}
        </button>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 rounded-xl p-3">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
          <span>Razorpay payments are credited instantly to your Saving Wallet using your provider's secure checkout. If your provider hasn't configured live keys yet, the demo flow simulates a successful payment.</span>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}
